import React, { createContext, useContext, useReducer } from 'react'
import { TravelSearchActions, TravelSearchState } from './travelSearchTypes'
import { getTrackingSession, TrackDates, TrackOneWay, TrackPassengers, TrackRoute, TrackVehicle } from '../Utils/tracking'
import { isFrsRoute } from '../Utils/frs-helpers'

const initialState: TravelSearchState = {
  selectedDates: { outboundDate: undefined, homeboundDate: undefined },
  selectedCurrency: 'EUR',
  multipleVehiclesChecked: false,
  multipleVehiclesSupported: false,
  petsOnlyAllowedInVehicle: false,
  disableOneWay: false,
  isOneWay: false,
  vehicleApiLoading: true,
  routesApiLoading: true,
  travelDatesApiLoading: true,
}

function reducer(state: TravelSearchState, action: TravelSearchActions): TravelSearchState {
  switch (action.type) {
    case 'SET_ROUTE':
      if (state.selectedRoute?.routeCode === action.route.routeCode) return state
      if (action.route.routeCode && state.vehicleApiData && getTrackingSession()) {
        TrackRoute(action.route, state.vehicleApiData)
      }
      return { ...state, selectedRoute: action.route }
    case 'SET_VEHICLE':
      if (
        state.selectedVehicle?.code === action.vehicle.code &&
        state.selectedVehicle.count === action.vehicle.count &&
        state.selectedVehicle.trailer === action.vehicle.trailer
      )
        return state
      if (
        action.vehicle &&
        state.vehicleApiData &&
        state.selectedRoute?.routeCode &&
        !isFrsRoute(state.selectedRoute?.routeCode) &&
        getTrackingSession()
      ) {
        TrackVehicle(action.vehicle, state.vehicleApiData)
      }
      return { ...state, selectedVehicle: action.vehicle }
    case 'SET_PASSENGERS':
      if (
        state.selectedPassengers?.adults === action.passengers.adults &&
        state.selectedPassengers?.children === action.passengers.children &&
        state.selectedPassengers?.childrenAges?.toString() === action.passengers.childrenAges?.toString() &&
        state.selectedPassengers?.pets === action.passengers.pets
      )
        return state
      if (action.passengers && state.selectedRoute && getTrackingSession()) {
        getTrackingSession() && action.passengers && TrackPassengers(action.passengers)
      }
      return { ...state, selectedPassengers: action.passengers }
    case 'SET_DATES':
      if (
        state.selectedDates.outboundDate === action.dates.outboundDate &&
        state.selectedDates.homeboundDate === action.dates.homeboundDate
      )
        return state
      if (
        getTrackingSession() &&
        ((!state.isOneWay && action.dates.outboundDate && action.dates.homeboundDate) || (state.isOneWay && action.dates.outboundDate))
      ) {
        TrackDates(action.dates, state.isOneWay)
      }
      return { ...state, selectedDates: action.dates }
    case 'SET_CURRENCY':
      if (state.selectedCurrency === action.currency) return state
      return { ...state, selectedCurrency: action.currency }
    case 'SET_MULTIPLE_VEHICLES_CHECKED':
      if (state.multipleVehiclesChecked === action.checked) return state
      return { ...state, multipleVehiclesChecked: action.checked }
    case 'SET_MULTIPLE_VEHICLES_SUPPORTED':
      if (state.multipleVehiclesSupported === action.supported) return state
      return { ...state, multipleVehiclesSupported: action.supported }
    case 'SET_PETS_ONLY_ALLOWED_IN_VEHICLE':
      if (state.petsOnlyAllowedInVehicle === action.allowed) return state
      return { ...state, petsOnlyAllowedInVehicle: action.allowed }
    case 'SET_DISABLE_ONE_WAY':
      if (state.disableOneWay === action.disabled) return state
      return { ...state, disableOneWay: action.disabled }
    case 'SET_TRAILER_NO_LONGER_AVAILABLE':
      if (state.selectedTrailerNoLongerAvailable?.code === action.trailer?.code) return state
      return { ...state, selectedTrailerNoLongerAvailable: action.trailer }
    case 'SET_ONEWAY':
      if (state.isOneWay === action.isOneWay) return state
      getTrackingSession() && action.isOneWay != undefined && TrackOneWay(action.isOneWay)
      return { ...state, isOneWay: action.isOneWay }
    // Set API data
    case 'SET_ROUTES_API_DATA':
      return { ...state, routesApiData: action.payload }
    case 'SET_VEHICLE_API_DATA':
      return { ...state, vehicleApiData: action.payload }
    case 'SET_OUTBOUND_TRAVEL_DATES':
      return { ...state, outboundTravelDates: action.payload }
    case 'SET_HOMEBOUND_TRAVEL_DATES':
      return { ...state, homeboundTravelDates: action.payload }

    // Set loading states
    case 'SET_ROUTES_API_LOADING':
      return { ...state, routesApiLoading: action.payload }
    case 'SET_VEHICLE_API_LOADING':
      return { ...state, vehicleApiLoading: action.payload }
    case 'SET_TRAVEL_DATES_API_LOADING':
      return { ...state, travelDatesApiLoading: action.payload }
    default:
      return state
  }
}

const TravelSearchContext = createContext<{
  state: TravelSearchState
  dispatch: React.Dispatch<TravelSearchActions>
} | null>(null)

export const TravelSearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  return <TravelSearchContext.Provider value={{ state, dispatch }}>{children}</TravelSearchContext.Provider>
}

export const useTravelSearchContext = () => {
  const context = useContext(TravelSearchContext)
  if (!context) {
    throw new Error('useTravelSearchContext must be used within a TravelSearchProvider')
  }
  return context
}
